import React, { useEffect, useRef, useState } from "react";
import { Camera, ArrowCounterClockwise, Check, X } from "@phosphor-icons/react";

/* Opens the front camera, lets the employee capture a selfie, returns a JPEG Blob via onCapture.
   Falls back to <input type=file capture> when getUserMedia is unavailable. */
export default function SelfieCapture({ title, onCapture, onCancel, onSkip }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [shot, setShot] = useState(null); // {blob, url}
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setError("Camera API not available — use the button below to take a photo."); return; }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        setReady(true);
      } catch (e) {
        setError("Camera permission denied or unavailable — use the button below to take a photo.");
      }
    };
    start();
    return () => { cancelled = true; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !ready) return;
    const w = 480, h = Math.round((v.videoHeight / v.videoWidth) * 480) || 360;
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.translate(w, 0); ctx.scale(-1, 1); // mirror like a selfie
    ctx.drawImage(v, 0, 0, w, h);
    c.toBlob((blob) => { if (blob) setShot({ blob, url: URL.createObjectURL(blob) }); }, "image/jpeg", 0.72);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setShot({ blob: f, url: URL.createObjectURL(f) });
  };

  const confirm = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); onCapture(shot.blob); };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/95 flex flex-col text-white" data-testid="selfie-capture">
      <div className="flex items-center justify-between px-5 pt-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#EA580C] font-semibold">Selfie verification</div>
          <div className="text-lg font-semibold">{title}</div>
        </div>
        <button onClick={onCancel} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center" data-testid="selfie-cancel"><X size={18} /></button>
      </div>
      <div className="flex-1 flex items-center justify-center px-5">
        <div className="w-full max-w-[360px] aspect-[4/3] rounded-sm overflow-hidden bg-black/60 relative">
          {!shot && <video ref={videoRef} playsInline muted className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} data-testid="selfie-video" />}
          {shot && <img src={shot.url} alt="selfie" className="w-full h-full object-cover" data-testid="selfie-preview" />}
          {!shot && !ready && !error && <div className="absolute inset-0 flex items-center justify-center text-sm text-white/70">Starting camera…</div>}
          {error && !shot && <div className="absolute inset-0 flex items-center justify-center text-[12.5px] text-white/80 px-6 text-center">{error}</div>}
        </div>
      </div>
      <div className="px-5 pb-8 space-y-3">
        {!shot ? (
          <>
            {ready && <button onClick={snap} className="w-full h-14 rounded-full bg-white text-[#0F172A] font-semibold flex items-center justify-center gap-2 active:scale-[0.99]" data-testid="selfie-snap"><Camera size={20} weight="fill" /> Take photo</button>}
            <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} data-testid="selfie-file-input" />
            <button onClick={() => fileRef.current?.click()} className={`w-full h-12 rounded-full border border-white/30 text-white/90 text-sm ${ready ? "" : "bg-white/10"}`} data-testid="selfie-file-btn">
              {ready ? "Use phone camera app instead" : "Open camera"}
            </button>
            {onSkip && <button onClick={onSkip} className="w-full text-[12px] text-white/60 underline underline-offset-4" data-testid="selfie-skip">Continue without photo</button>}
          </>
        ) : (
          <div className="flex gap-3">
            <button onClick={() => setShot(null)} className="flex-1 h-14 rounded-full border border-white/30 text-white flex items-center justify-center gap-2" data-testid="selfie-retake"><ArrowCounterClockwise size={18} /> Retake</button>
            <button onClick={confirm} className="flex-[2] h-14 rounded-full bg-[#EA580C] text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.99]" data-testid="selfie-confirm"><Check size={20} weight="bold" /> Use photo & punch</button>
          </div>
        )}
      </div>
    </div>
  );
}
